import React, { useState, useRef, useEffect } from "react";
import { Globe, Check, ChevronDown } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";

const CURRENCIES = [
    { id: "ALL", label: "Lek (L)" },
    { id: "EUR", label: "Euro (€)" },
];

const LangCurrencySwitcher = ({ compact = false }) => {
    const { lang, setLang, currency, setCurrency, t, LANGUAGES } = useI18n();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const activeLang = LANGUAGES.find((l) => l.id === lang);

    return (
        <div className="relative" ref={ref}>
            <button
                data-testid="lang-currency-switcher"
                onClick={() => setOpen((v) => !v)}
                className={`h-10 px-3 rounded-full border flex items-center gap-2 transition-all ${
                    compact
                        ? "bg-white/10 border-white/20 text-white hover:bg-white/20"
                        : "bg-white border-neutral-200 hover:border-[#FF5722] hover:shadow-sm"
                }`}
            >
                <Globe className="h-4 w-4" />
                <span className="text-sm font-semibold">{activeLang?.flag}</span>
                <span className="text-xs px-1.5 py-0.5 rounded-full font-bold bg-neutral-100 text-neutral-700">
                    {currency === "ALL" ? "L" : "€"}
                </span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
                <div
                    data-testid="lang-currency-menu"
                    className="absolute right-0 top-full mt-2 w-60 bg-white rounded-2xl shadow-2xl border border-neutral-200 p-2 z-50"
                >
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                        {t("ui_language")}
                    </div>
                    {LANGUAGES.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`lang-option-${l.id}`}
                            onClick={() => setLang(l.id)}
                            className={`w-full px-3 py-2.5 rounded-xl flex items-center justify-between text-left hover:bg-neutral-100 transition-colors ${
                                lang === l.id ? "bg-[#FFF3EE] text-[#FF5722]" : "text-neutral-800"
                            }`}
                        >
                            <span className="flex items-center gap-2.5 text-sm font-medium">
                                <span className="text-base">{l.flag}</span>
                                {l.label}
                            </span>
                            {lang === l.id && <Check className="h-4 w-4" />}
                        </button>
                    ))}

                    <div className="border-t border-neutral-100 my-2" />

                    <div className="px-3 pt-1 pb-1 text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">
                        {t("ui_currency")}
                    </div>
                    {CURRENCIES.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`currency-option-${c.id}`}
                            onClick={() => !c.disabled && setCurrency(c.id)}
                            disabled={c.disabled}
                            aria-disabled={c.disabled}
                            title={c.disabled ? t("category_coming_soon") : undefined}
                            className={`w-full px-3 py-2.5 rounded-xl flex items-center justify-between text-left transition-colors ${
                                c.disabled
                                    ? "text-neutral-400 cursor-not-allowed opacity-60"
                                    : `hover:bg-neutral-100 ${currency === c.id ? "bg-[#FFF3EE] text-[#FF5722]" : "text-neutral-800"}`
                            }`}
                        >
                            <span className="text-sm font-medium">{c.label}</span>
                            {c.disabled ? (
                                <span className="text-[9px] font-bold uppercase tracking-wider bg-neutral-200 text-neutral-600 px-1.5 py-0.5 rounded-full">
                                    {t("category_coming_soon")}
                                </span>
                            ) : (
                                currency === c.id && <Check className="h-4 w-4" />
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LangCurrencySwitcher;
